<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceViewDocumentDropdown',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const icons = ref({
    printerOn: 'icon-park-outline:printer-one',
    printerOff: 'icon-park-outline:doc-fail',
  });
  const ux = ref([]);

  const $data = computed(() => ({
    icons: icons.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'icons' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { icons, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isVisible = computed(() => {
    return (
      props.invoice.meta &&
      (props.invoice.meta.has_print_document ||
        props.invoice.meta.invoice_view_upload_enabled ||
        props.invoice.meta.invoice_view_delete_enabled)
    );
  });
  const noSplit = computed(() => {
    return (
      props.invoice.meta &&
      !props.invoice.meta.invoice_view_upload_enabled &&
      !props.invoice.meta.invoice_view_delete_enabled
    );
  });
  const printerIcon = computed(() => {
    if (loaded.value) {
      return props.invoice.meta && props.invoice.meta.has_print_document
        ? icons.value.printerOn
        : icons.value.printerOff;
    }
    return null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  const act_6804155476809633_script_yxdvbagh0_value_callable = async (
    $event
  ) => {
    return $fn.value.showFileInNewTab(props.invoice.links.print_url);
  };
  const act_6804155476809633_script_yxdvbagh0_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6804155476809633_script_yxdvbagh0_value_callable($event);
      },
    };
  };
  const act_5974459770896956_emit_iypsp4572_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5974459770896956_showModal_vd5dri9jz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'FileSelector',
      props: {
        url: props.invoice.links.print_url,
        accept: props.invoice.meta.accepted_print_document_formats,
      },
    };
  };
  const act_8852205820859826_emit_4hnxxozpo_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_8852205820859826_rest_5pj1sztw6_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      resource: props.invoice,
    };
  };
  const act_8852205820859826_rest_5pj1sztw6_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_6804155476809633_script_yxdvbagh0 = new actionTypes.ScriptAction({
    actionArgs: act_6804155476809633_script_yxdvbagh0_getActionArgs,
    displayName: 'openDocument',
  });
  const inst_evh_6804155476809633_splitClick_rq49lejit =
    new actionTypes.EventHandlerImpl({
      action: act_6804155476809633_script_yxdvbagh0,
      event: 'split-click',
      displayName: 'openDocument',
    });
  const inst_evh_6804155476809633_splitClick =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6804155476809633_splitClick_rq49lejit],
      event: 'split-click',
    });
  const act_5974459770896956_emit_iypsp4572 = new actionTypes.EmitAction({
    actionArgs: act_5974459770896956_emit_iypsp4572_getActionArgs,
  });
  const inst_evh_5974459770896956_close_ldibfwlaw =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_emit_iypsp4572,
      event: 'close',
      displayName: 'emit',
    });
  const act_5974459770896956_showModal_vd5dri9jz =
    new actionTypes.ShowModalAction({
      actionArgs: act_5974459770896956_showModal_vd5dri9jz_getActionArgs,
      events: [inst_evh_5974459770896956_close_ldibfwlaw],
    });
  const inst_evh_5974459770896956_clickPrevent_dscpt3gw6 =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_showModal_vd5dri9jz,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_5974459770896956_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5974459770896956_clickPrevent_dscpt3gw6],
      event: 'click.prevent',
    });
  const act_8852205820859826_emit_4hnxxozpo = new actionTypes.EmitAction({
    actionArgs: act_8852205820859826_emit_4hnxxozpo_getActionArgs,
  });
  const inst_evh_8852205820859826_success_3ct2vl7ma =
    new actionTypes.EventHandlerImpl({
      action: act_8852205820859826_emit_4hnxxozpo,
      event: 'success',
      displayName: 'emit',
    });
  const act_8852205820859826_rest_5pj1sztw6 = new actionTypes.RestAction({
    actionArgs: act_8852205820859826_rest_5pj1sztw6_getActionArgs,
    confirm: act_8852205820859826_rest_5pj1sztw6_getConfirm,
    events: [inst_evh_8852205820859826_success_3ct2vl7ma],
  });
  const inst_evh_8852205820859826_clickPrevent_au5fcyeih =
    new actionTypes.EventHandlerImpl({
      action: act_8852205820859826_rest_5pj1sztw6,
      event: 'click.prevent',
      displayName: 'rest',
    });
  const inst_evh_8852205820859826_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8852205820859826_clickPrevent_au5fcyeih],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_6804155476809633_splitClick_rq49lejit,
    openDocument: inst_evh_6804155476809633_splitClick_rq49lejit,
    inst_evh_6804155476809633_splitClick,
    inst_evh_5974459770896956_close_ldibfwlaw,
    inst_evh_5974459770896956_clickPrevent_dscpt3gw6,
    inst_evh_5974459770896956_clickPrevent,
    inst_evh_8852205820859826_success_3ct2vl7ma,
    inst_evh_8852205820859826_clickPrevent_au5fcyeih,
    inst_evh_8852205820859826_clickPrevent,
  };

  const evh_6804155476809633_splitClick_rq49lejit = async (event, scope) => {
    return await actions.inst_evh_6804155476809633_splitClick_rq49lejit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6804155476809633_splitClick = async (event, scope) => {
    return await actions.inst_evh_6804155476809633_splitClick.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_close_ldibfwlaw = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_close_ldibfwlaw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPrevent_dscpt3gw6 = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPrevent_dscpt3gw6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8852205820859826_success_3ct2vl7ma = async (event, scope) => {
    return await actions.inst_evh_8852205820859826_success_3ct2vl7ma.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8852205820859826_clickPrevent_au5fcyeih = async (event, scope) => {
    return await actions.inst_evh_8852205820859826_clickPrevent_au5fcyeih.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8852205820859826_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8852205820859826_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :disabled="!invoice.meta.has_print_document"
    variant="light"
    :icon="printerIcon"
    :href="invoice.links.print_url"
    target="_blank"
    use-auth
    show-spinner
    v-if="isVisible && loaded && noSplit"
  >
  </ItpButton>
  <BDropdown
    variant="light"
    split
    class="ms-auto me-2 border"
    v-if="isVisible && loaded && !noSplit"
    @split-click="evh_6804155476809633_splitClick($event, {})"
  >
    <template
      :title="pgettext('Invoice', 'Open print document')"
      #button-content
    >
      <BSpinner
        spinning
        type="grow"
        class="me-1"
        v-if="actions.openDocument && actions.openDocument.isRunning"
      >
      </BSpinner>
      <div
        :title="
          pgettext('Invoice', 'Open print document in a new browser tab.')
        "
        :class="[
          'me-1',
          !invoice.meta.has_print_document ? 'text-muted' : null,
        ]"
      >
        <ItpIcon :name="printerIcon"> </ItpIcon>
      </div>
    </template>
    <BDropdownItem
      v-if="invoice.meta.invoice_view_upload_enabled"
      @click.prevent="evh_5974459770896956_clickPrevent($event, {})"
    >
      <ItpText :text="pgettext('Invoice|Command', 'Upload print document')">
      </ItpText>
    </BDropdownItem>
    <BDropdownItem
      v-if="
        invoice.meta.has_print_document &&
        invoice.meta.invoice_view_delete_enabled
      "
      @click.prevent="evh_8852205820859826_clickPrevent($event, {})"
    >
      <ItpText :text="pgettext('Invoice|Command', 'Delete print document')">
      </ItpText>
    </BDropdownItem>
  </BDropdown>
</template>
